import { readField } from "./fields.ts";
import { parseFrontMatter, splitDocument } from "./front-matter.ts";
import { type Field, type Schema, THOUGHTS } from "./schema.ts";

export interface DocSummary {
  path: string;
  /** Front matter with every required key. */
  isThought: boolean;
  hasFrontMatter: boolean;
  title: string;
  date: string;
  tags: string[];
}

/**
 * What the sidebar needs to know about a document, read from its text.
 * @param {string} path
 * @param {string} text
 * @param {typeof THOUGHTS} [schema]
 * @returns {DocSummary}
 */
export function summarize(path: string, text: string, schema: Schema = THOUGHTS): DocSummary {
  const parts = splitDocument(text);
  const summary: DocSummary = {
    path,
    isThought: false,
    hasFrontMatter: parts.hasFrontMatter,
    title: "",
    date: "",
    tags: [],
  };
  if (!parts.hasFrontMatter) return summary;

  const { doc, errors } = parseFrontMatter(parts.yaml);
  if (errors.length > 0) return summary;

  const byKey = (key: string): Field =>
    schema.fields.find((f) => f.key === key) ?? { key, label: key, type: "text" };
  summary.isThought = schema.required.every((key) => doc.has(key));
  summary.title = readField(doc, byKey(schema.titleKey)) as string;
  summary.date = readField(doc, byKey(schema.dateKey)) as string;
  summary.tags = readField(doc, byKey(schema.tagsKey)) as string[];
  return summary;
}

/**
 * The text of a new Thought: front matter in schema order, then an empty body.
 * @param {{ title: string, date: string }} values
 * @param {typeof THOUGHTS} [schema]
 */
export function newThoughtText(
  { title, date }: { title: string; date: string },
  schema: Schema = THOUGHTS,
): string {
  const lines = [];
  for (const field of schema.fields) {
    if (field.type === "tags") {
      lines.push(`${field.key}: []`);
    } else if (field.type === "boolean") {
      lines.push(`${field.key}: false`);
    } else if (field.key === schema.dateKey) {
      lines.push(`${field.key}: ${date}`);
    } else {
      lines.push(`${field.key}: ${JSON.stringify(field.key === schema.titleKey ? title : "")}`);
    }
  }
  return `---\n${lines.join("\n")}\n---\n\n`;
}
